import React, { useState, useEffect, Suspense } from 'react';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import { PermissionsProvider } from './PermissionsContext';
import './App.css';

const Clients     = React.lazy(() => import('./components/Clients'));
const Invoices    = React.lazy(() => import('./components/Invoices'));
const BankImport  = React.lazy(() => import('./components/BankImport'));
const Reports     = React.lazy(() => import('./components/Reports'));
const Assets      = React.lazy(() => import('./components/Assets'));
const Payroll     = React.lazy(() => import('./components/Payroll'));
const Products    = React.lazy(() => import('./components/Products'));
const Recurring   = React.lazy(() => import('./components/Recurring'));
const Settings    = React.lazy(() => import('./components/Settings'));
const Suppliers   = React.lazy(() => import('./components/Suppliers'));
const Expenses    = React.lazy(() => import('./components/Expenses'));
const Permissions = React.lazy(() => import('./components/Permissions'));
const Inventory   = React.lazy(() => import('./components/Inventory'));
const Warehouses  = React.lazy(() => import('./components/Warehouses'));
const Projects    = React.lazy(() => import('./components/Projects'));
const MobileScan  = React.lazy(() => import('./components/MobileScan'));
const Stocktake   = React.lazy(() => import('./components/Stocktake'));
const Service     = React.lazy(() => import('./components/Service'));
const ApiTokens   = React.lazy(() => import('./components/ApiTokens'));
const OAuthAuthorize = React.lazy(() => import('./components/OAuthAuthorize'));

function App() {
  const [user, setUser] = useState(null);
  const [page, setPage] = useState('dashboard');
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try { setUser(JSON.parse(savedUser)); } catch { localStorage.clear(); }
    }
  }, []);

  // Close mobile sidebar whenever page changes
  useEffect(() => { setMobileOpen(false); }, [page]);

  const handleLogin = (data) => {
    localStorage.setItem('user', JSON.stringify(data.user));
    if (data.currentCompanyId != null) {
      localStorage.setItem('currentCompanyId', String(data.currentCompanyId));
    }
    setUser(data.user);
  };

  const handleLogout = async () => {
    try { await import('./api').then(m => m.default.post('/auth/logout')); } catch { /* ignore */ }
    localStorage.removeItem('user');
    localStorage.removeItem('currentCompanyId');
    setUser(null);
  };

  // OAuth 2.1 consent lapa — pilnībā neatkarīga no galvenā app user/page state
  // (claude.ai/mobile connector navigē šeit tieši, arī ja lietotājam JAU ir
  // atvērta app cilne citur; nedrīkst to izmest ārā vai sajaukt stāvokļus).
  if (window.location.pathname === '/oauth/authorize') {
    return (
      <Suspense fallback={<div style={{ padding: 40 }}>Ielādē...</div>}>
        <OAuthAuthorize />
      </Suspense>
    );
  }

  if (!user) return <Login onLogin={handleLogin} />;

  const pages = {
    dashboard:   <Dashboard setPage={setPage} />,
    clients:     <Clients />,
    products:    <Products />,
    invoices:    <Invoices />,
    recurring:   <Recurring />,
    bank:        <BankImport />,
    suppliers:   <Suppliers />,
    expenses:    <Expenses />,
    reports:     <Reports />,
    assets:      <Assets />,
    payroll:     <Payroll />,
    settings:    <Settings />,
    permissions: <Permissions />,
    inventory:   <Inventory />,
    warehouses:  <Warehouses />,
    projects:    <Projects />,
    'mobile-scan': <MobileScan />,
    stocktake:     <Stocktake />,
    service:       <Service />,
    'api-tokens':  <ApiTokens />,
  };

  return (
    <PermissionsProvider>
      <div className={`app-layout${mobileOpen ? ' mobile-open' : ''}`}>
        <Sidebar page={page} setPage={setPage} user={user} onLogout={handleLogout} />
        {mobileOpen && <div className="sidebar-backdrop" onClick={() => setMobileOpen(false)} />}
        <div className="main-content">
          <button className="mobile-menu-btn" onClick={() => setMobileOpen(v => !v)} aria-label="Atvērt izvēlni">
            <span /><span /><span />
          </button>
          <Suspense fallback={<div className="page-content" style={{ color: '#6b7280' }}>Ielādē...</div>}>
            {pages[page] || <Dashboard setPage={setPage} />}
          </Suspense>
        </div>
      </div>
    </PermissionsProvider>
  );
}

export default App;
